import { ReactNode, useEffect, useMemo, useState } from 'react';
import { Card, Col, Form, Row, Button, Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import Select from 'components/UnifiedSelect';

import Div from 'components/Div';
import FormInput from 'components/FormInput/index';
import PageTitle from 'components/PageTitle/index';
import { getComplaintFilterOptions, getComplaintOverview } from '../api';
import { loadSharedComplaintFilters, saveSharedComplaintFilters } from '../filters/sharedFilters';
import loader from 'assets/images/datahub-loader.svg';

import 'assets/scss/custom/pages/_rankings.scss';
import './ComplaintsOverview.scss';

type FilterFormValues = {
  company: string;
  brand: string;
  windowRange: Date[] | null;
  compareRange: Date[] | null;
  customerClass: string;
};

type ComplaintsOverviewData = {
  filters: {
    brandId: string;
    brandName: string;
    windowMonths: number;
    compareMonths: number;
    customerClass: string;
  };
  summary: {
    t12mComplaints: number;
    priorComplaints: number;
    latestAvailableMonth?: string;
    currentStartMonth?: string;
    currentEndMonth?: string;
    priorStartMonth?: string;
    priorEndMonth?: string;
    topState: string;
    topStateNote: string;
    topComplaintType: string;
    topComplaintTypeNote: string;
    mostAffectedClass: string;
    mostAffectedClassNote: string;
  };
  byState: Array<{ label: string; value: number; color: string; priorityName?: string | null; priorityScore?: number }>;
  byType: Array<{ label: string; value: number; color: string; priorityName?: string | null; priorityScore?: number }>;
  stateComparison: Array<{ label: string; deltaPct: number; color: string }>;
  takeaways: Array<{ kind: string; text: string; emphasis: string }>;
};

const getMonthStart = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);
const getMonthRange = (monthCount: number, endDate = new Date()): [Date, Date] => {
  const end = getMonthStart(endDate);
  const start = new Date(end.getFullYear(), end.getMonth() - (monthCount - 1), 1);
  return [start, end];
};

const buildEmptyOverviewData = (filters: { brand: string; customerClass: string }): ComplaintsOverviewData => ({
  filters: {
    brandId: filters.brand || '',
    brandName: filters.brand === ALL_VALUE ? 'All Brands' : filters.brand || '',
    windowMonths: 0,
    compareMonths: 0,
    customerClass: filters.customerClass || 'All',
  },
  summary: {
    t12mComplaints: 0,
    priorComplaints: 0,
    topState: '--',
    topStateNote: 'largest footprint',
    topComplaintType: '--',
    topComplaintTypeNote: 'persistent',
    mostAffectedClass: filters.customerClass || 'All',
    mostAffectedClassNote: 'selected filter context',
  },
  byState: [],
  byType: [],
  stateComparison: [],
  takeaways: [],
});
const toMonthParam = (value: unknown) => {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return `${value.getFullYear()}-${String(value.getMonth() + 1).padStart(2, '0')}-01`;
  }
  return null;
};
const formatMonthLabel = (value?: Date | null) => {
  if (!value) return '';
  return value.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
};

const classOptions = [
  { id: 'All', name: 'All', value: 'All' },
  { id: 'Residential', name: 'Residential', value: 'Residential' },
  { id: 'Commercial', name: 'Commercial', value: 'Commercial' },
  { id: 'Industrial', name: 'Industrial', value: 'Industrial' },
];
const ALL_VALUE = 'All';

const renderTitleWithRange = (title: string) => {
  const parts = title.split(/(\s\([^)]+\))/g).filter(Boolean);
  if (parts.length <= 1) return <span>{title}</span>;

  return <span>{parts.map((part, index) => (/^\s\([^)]+\)$/.test(part) ? <span key={index} className="complaints-overview__panel-title-range">{part}</span> : <span key={index}>{part}</span>))}</span>;
};

const SummaryCard = ({
  eyebrow,
  primary,
  secondary,
  infoTooltip,
}: {
  eyebrow: string;
  primary: string;
  secondary?: string;
  infoTooltip?: ReactNode;
}) => (
  <div className="complaints-overview__summary-card h-100">
    <div className="complaints-overview__eyebrow complaints-overview__summary-eyebrow d-flex align-items-center gap-2">
      <span>{eyebrow}</span>
      {infoTooltip ? (
        <OverlayTrigger placement="top" overlay={<Tooltip id={`overview-summary-tooltip-${eyebrow.replace(/\s+/g, '-').toLowerCase()}`}>{infoTooltip}</Tooltip>}>
          <i className="mdi mdi-information-outline" role="button" aria-label={`${eyebrow} details`} />
        </OverlayTrigger>
      ) : null}
    </div>
    <div className="d-flex align-items-center gap-2 flex-wrap complaints-overview__summary-content">
      <div className="complaints-overview__hero-number">
        {primary}
      </div>
      {secondary ? <div className="complaints-overview__hero-note">{secondary}</div> : null}
    </div>
  </div>
);

const CountSummaryCard = ({
  current,
  prior,
  windowLabel,
  infoTooltip,
}: {
  current: number;
  prior: number;
  windowLabel: string;
  infoTooltip?: ReactNode;
}) => {
  const delta = current - prior;
  const deltaText = `${Math.abs(delta)} vs ${prior} prior`;
  const deltaClass = delta > 0 ? 'complaints-overview__trend-up' : delta < 0 ? 'complaints-overview__trend-down' : 'complaints-overview__trend-flat';
  const deltaIcon = delta > 0 ? 'mdi mdi-arrow-up' : delta < 0 ? 'mdi mdi-arrow-down' : 'mdi mdi-minus';

  return (
    <div className="complaints-overview__summary-card h-100">
      <div className="complaints-overview__eyebrow complaints-overview__summary-eyebrow d-flex align-items-center gap-2">
        <span>{windowLabel} Complaints</span>
        {infoTooltip ? (
          <OverlayTrigger placement="top" overlay={<Tooltip id="overview-count-window-tooltip">{infoTooltip}</Tooltip>}>
            <i className="mdi mdi-information-outline" role="button" aria-label="Window period details" />
          </OverlayTrigger>
        ) : null}
      </div>
      <div className="d-flex align-items-center gap-2 flex-wrap complaints-overview__summary-content">
        <div className="complaints-overview__hero-number">{current}</div>
        <div className={`complaints-overview__hero-delta ${deltaClass}`}><i className={deltaIcon} /> {deltaText}</div>
      </div>
    </div>
  );
};

const FilterCardLoader = () => (
  <div className="mb-4 ranking-filters">
    <div className="row g-3 ranking-section align-items-end">
      {[0, 1, 2, 3].map((key) => (
        <div key={key} className="col-12 col-md-6 col-xl">
          <div className="complaints-overview__placeholder-line complaints-overview__placeholder-line--sm mb-2" />
          <div className="complaints-overview__placeholder-line complaints-overview__placeholder-line--md complaints-overview__filter-skeleton" />
        </div>
      ))}
      <div className="col-12 col-xl-auto complaints-overview__actions-col">
        <div className="d-flex justify-content-xl-end gap-2 complaints-overview__actions">
          <div className="complaints-overview__placeholder-line complaints-overview__button-skeleton" />
          <div className="complaints-overview__placeholder-line complaints-overview__button-skeleton" />
        </div>
      </div>
    </div>
    <div className="row g-3 align-items-start align-items-lg-center ranking-section mt-1">
      <div className="col-12 col-md-auto section-heading-column d-flex align-items-start align-items-lg-center">
        <h5>Filters</h5>
      </div>
      <div className="col">
        <div className="d-flex flex-wrap gap-2">
          {[0, 1, 2].map((key) => (
            <div key={key} className="complaints-overview__placeholder-line complaints-overview__pill-skeleton" />
          ))}
        </div>
      </div>
    </div>
  </div>
);

const ChartPanel = ({
  title,
  data,
  loading,
  infoTooltip,
}: {
  title: string;
  data: Array<{ label: string; value: number; color: string }>;
  loading: boolean;
  infoTooltip?: ReactNode;
}) => {
  const hasDenseAxis = data.length > 8;

  return (
    <div className="complaints-overview__panel h-100">
      <div className="complaints-overview__panel-title d-flex align-items-center gap-2">
        {renderTitleWithRange(title)}
        {infoTooltip ? (
          <OverlayTrigger placement="top" overlay={<Tooltip id={`overview-chart-tooltip-${title.replace(/\s+/g, '-').toLowerCase()}`}>{infoTooltip}</Tooltip>}>
            <i className="mdi mdi-information-outline" role="button" aria-label="Window period details" />
          </OverlayTrigger>
        ) : null}
      </div>
      {loading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ height: 220 }}>
          <img src={loader} height={44} alt="Loading" />
        </div>
      ) : data.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center text-muted" style={{ height: 220 }}>
          Data is not available for the selected filters.
        </div>
      ) : (
        <div className="complaints-overview__chart-shell">
          <ResponsiveContainer width="100%" height="100%">
            {hasDenseAxis ? (
              <BarChart data={data} layout="vertical" margin={{ top: 8, right: 28, left: 12, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis
                  dataKey="label"
                  type="category"
                  axisLine={false}
                  tickLine={false}
                  width={86}
                  interval={0}
                  tick={{
                    fill: '#6c7a92',
                    fontSize: 12,
                  }}
                  tickMargin={8}
                />
                <Bar dataKey="value" radius={[0, 10, 10, 0]} maxBarSize={14}>
                  <LabelList dataKey="value" position="right" style={{ fill: '#213047', fontSize: 12, fontWeight: 600 }} />
                  {data.map((entry) => (
                    <Cell key={entry.label} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            ) : (
              <BarChart data={data} margin={{ top: 28, right: 8, left: 0, bottom: 0 }}>
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  interval={0}
                  minTickGap={0}
                  height={30}
                  tick={{
                    fill: '#6c7a92',
                    fontSize: 12,
                  }}
                  tickMargin={6}
                />
                <YAxis hide />
                <Bar dataKey="value" radius={[10, 10, 0, 0]} maxBarSize={86}>
                  <LabelList dataKey="value" position="top" style={{ fill: '#213047', fontSize: 12, fontWeight: 600 }} />
                  {data.map((entry) => (
                    <Cell key={entry.label} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

const ComparisonPanel = ({
  title,
  data,
  loading,
  infoTooltip,
}: {
  title: string;
  data: ComplaintsOverviewData['stateComparison'];
  loading: boolean;
  infoTooltip?: ReactNode;
}) => {
  const maxDelta = Math.max(...data.map((entry) => Math.abs(entry.deltaPct)), 1);
  const tooltipContent = infoTooltip || 'Window periods are based on latest available complaint month in data.';

  return (
    <div className="complaints-overview__panel h-100">
      <div className="complaints-overview__panel-title d-flex align-items-center gap-2">
        {renderTitleWithRange(title)}
        <OverlayTrigger placement="top" overlay={<Tooltip id="overview-window-tooltip">{tooltipContent}</Tooltip>}>
          <i className="mdi mdi-information-outline" role="button" aria-label="Window period details" />
        </OverlayTrigger>
      </div>
      {loading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 180 }}>
          <img src={loader} height={44} alt="Loading" />
        </div>
      ) : data.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 180 }}>
          Comparison data is not available for the selected filters.
        </div>
      ) : (
        <>
          <div className="complaints-overview__comparison-scale">
            <span className="complaints-overview__comparison-scale-spacer" />
            <span className="complaints-overview__comparison-scale-track">
              <span>-{maxDelta.toFixed(1)}%</span>
              <span>0%</span>
              <span>+{maxDelta.toFixed(1)}%</span>
            </span>
            <span className="complaints-overview__comparison-scale-value-spacer" />
          </div>
          <div className="complaints-overview__comparison-row">
            {data.map((item) => {
              const hasDelta = item.deltaPct !== 0;
              const width = `${Math.max((Math.abs(item.deltaPct) / maxDelta) * 50, 6)}%`;

              return (
                <div key={item.label} className="complaints-overview__comparison-item">
                  <div className="complaints-overview__comparison-label">{item.label}</div>
                  <div className="complaints-overview__comparison-track complaints-overview__comparison-track--centered">
                    <div className="complaints-overview__comparison-zero" />
                    {hasDelta ? (
                      <div
                        className={`complaints-overview__comparison-fill ${item.deltaPct < 0 ? 'complaints-overview__comparison-fill--negative' : 'complaints-overview__comparison-fill--positive'}`}
                        style={{ width, backgroundColor: item.color }}
                      />
                    ) : null}
                  </div>
                  <div className="complaints-overview__comparison-value">
                    {item.deltaPct > 0 ? '+' : ''}
                    {item.deltaPct}%
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

const TakeawaysPanel = ({ data, loading }: { data: ComplaintsOverviewData['takeaways']; loading: boolean }) => (
  <div className="complaints-overview__panel h-100">
      <div className="complaints-overview__panel-title">Broker takeaway</div>
      {loading ? (
        <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 180 }}>
          <img src={loader} height={44} alt="Loading" />
        </div>
      ) : data.length === 0 ? (
        <div className="d-flex align-items-center justify-content-center text-muted" style={{ minHeight: 180 }}>
          No takeaway available for current selection.
        </div>
      ) : (
        <div className="complaints-overview__takeaway-list">
          {data.map((item) => (
            <div key={item.text} className="complaints-overview__takeaway-item">
              <p className="complaints-overview__takeaway-text">{item.text}</p>
              <div className="complaints-overview__takeaway-meta">
                <span className="badge badge-outline-secondary complaints-overview__badge">{item.kind}</span>
                <span className="complaints-overview__takeaway-emphasis">{item.emphasis}</span>
              </div>
            </div>
          ))}
        </div>
      )}
  </div>
);

const ComplaintsOverview = () => {
  const initialFilters = {
    company: '',
    brand: '',
    windowRange: getMonthRange(12),
    compareRange: getMonthRange(12, new Date(new Date().getFullYear(), new Date().getMonth() - 12, 1)),
    customerClass: classOptions[0].value,
  };

  const { register, handleSubmit, reset, setValue, watch, control } = useForm<FilterFormValues>({
    defaultValues: {
      ...initialFilters,
    },
  });
  const [defaultFilters, setDefaultFilters] = useState<FilterFormValues>(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState<FilterFormValues>(initialFilters);
  const [companyChoices, setCompanyChoices] = useState<Array<{ id: string; name: string; value: string }>>([]);
  const [brandChoices, setBrandChoices] = useState<Array<{ id: string; name: string; value: string }>>([]);
  const [companyBrandsMap, setCompanyBrandsMap] = useState<Record<string, Array<{ id: string; name: string; value: string }>>>({});
  const [filterOptionsSnapshot, setFilterOptionsSnapshot] = useState<{ customerClasses?: string[] }>({});
  const [classChoices, setClassChoices] = useState(classOptions);
  const [data, setData] = useState<ComplaintsOverviewData>({
    filters: { brandId: '', brandName: '', windowMonths: 12, compareMonths: 12, customerClass: 'All' },
    summary: { t12mComplaints: 0, priorComplaints: 0, topState: '-', topStateNote: 'largest footprint', topComplaintType: '-', topComplaintTypeNote: 'persistent', mostAffectedClass: 'All', mostAffectedClassNote: 'where available' },
    byState: [],
    byType: [],
    stateComparison: [],
    takeaways: [],
  });
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [hasOverviewData, setHasOverviewData] = useState(false);
  const showContentLoading = filtersLoading || dataLoading || !hasOverviewData;
  const selectedCompanyValue = watch('company');
  const selectedCompanyOption = useMemo(() => companyChoices.find((option) => option.value === selectedCompanyValue) || null, [companyChoices, selectedCompanyValue]);
  const selectedBrandValue = watch('brand');
  const selectedBrandOption = useMemo(() => brandChoices.find((option) => option.value === selectedBrandValue) || null, [brandChoices, selectedBrandValue]);
  const selectedClassValue = watch('customerClass');
  const selectedClassOption = useMemo(() => classChoices.find((option) => option.value === selectedClassValue) || null, [classChoices, selectedClassValue]);

  const brandName = brandChoices.find((item) => item.value === appliedFilters.brand)?.name || '';
  const selectedWindowRange = Array.isArray(appliedFilters.windowRange) ? appliedFilters.windowRange : [];
  const selectedCompareRange = Array.isArray(appliedFilters.compareRange) ? appliedFilters.compareRange : [];
  const selectedWindowLabel = selectedWindowRange.length === 2 ? `${formatMonthLabel(selectedWindowRange[0])}-${formatMonthLabel(selectedWindowRange[1])}` : '';
  const selectedCompareLabel = selectedCompareRange.length === 2 ? `${formatMonthLabel(selectedCompareRange[0])}-${formatMonthLabel(selectedCompareRange[1])}` : '';

  const selectedFilterPills = useMemo(
    () => [
      ['Company', companyChoices.find((item) => item.value === appliedFilters.company)?.name],
      ['Brand', brandChoices.find((item) => item.value === appliedFilters.brand)?.name],
      ['Window', selectedWindowLabel],
      ['Compare', selectedCompareLabel],
      ['Class', classChoices.find((item) => item.value === appliedFilters.customerClass)?.name],
    ],
    [appliedFilters, companyChoices, brandChoices, classChoices, selectedWindowLabel, selectedCompareLabel]
  );

  const activeWindowLabel = 'Window';
  const activeCompareLabel = 'Compare window';

  const formatMonth = (value?: string) => {
    if (!value) return '-';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };

  const currentRangeLabel = useMemo(() => {
    const start = formatMonth(data.summary.currentStartMonth);
    const end = formatMonth(data.summary.currentEndMonth);
    return start !== '-' && end !== '-' ? `${start}-${end}` : '';
  }, [data.summary.currentStartMonth, data.summary.currentEndMonth]);

  const priorRangeLabel = useMemo(() => {
    const start = formatMonth(data.summary.priorStartMonth);
    const end = formatMonth(data.summary.priorEndMonth);
    return start !== '-' && end !== '-' ? `${start}-${end}` : '';
  }, [data.summary.priorStartMonth, data.summary.priorEndMonth]);

  const countTooltip = 'Total complaint count for the selected brand and class in the current window, compared to the prior window total.';
  const topStateTooltip = 'State with the highest complaint count in the selected current window.';
  const topTypeTooltip = 'Complaint type with the highest total count after type mapping in the selected current window.';
  const topClassTooltip = 'Customer class with the highest complaint count in the selected current window.';
  const byStateTooltip = 'Complaint totals grouped by state for the selected current window.';
  const byTypeTooltip = 'Complaint totals grouped by mapped complaint type for the selected current window.';
  const comparisonTooltip = 'State-wise percent change between the selected current window and prior comparison window.';

  useEffect(() => {
    document.title = 'DataHub - Complaints Overview';

    const loadFilters = async () => {
      try {
        const filterOptions = await getComplaintFilterOptions();
        setFilterOptionsSnapshot(filterOptions || {});
        const companies = (filterOptions?.companies || []).map((item: { companyId: string; companyName: string }) => ({
          id: item.companyId,
          name: item.companyName,
          value: item.companyId,
        }));
        const companyBrands = (filterOptions?.companyBrands || []) as Array<{ companyId: string; brandId: string; brandName: string }>;
        const nextCompanyBrandsMap = companyBrands.reduce((acc, row) => {
          if (!row?.companyId || !row?.brandId) return acc;
          if (!acc[row.companyId]) acc[row.companyId] = [];
          if (!acc[row.companyId].some((item) => item.value === row.brandId)) {
            acc[row.companyId].push({ id: row.brandId, name: row.brandName, value: row.brandId });
          }
          return acc;
        }, {} as Record<string, Array<{ id: string; name: string; value: string }>>);
        Object.values(nextCompanyBrandsMap).forEach((rows) => rows.sort((a, b) => a.name.localeCompare(b.name)));
        const defaultCompany = companies[0]?.value || '';
        const brands = nextCompanyBrandsMap[defaultCompany] || [];
        const brandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...brands];
        const stored = loadSharedComplaintFilters();
        const selectedCompany = companies.some((item) => item.value === stored.company) ? (stored.company as string) : defaultCompany;
        const selectedBrands = nextCompanyBrandsMap[selectedCompany] || [];
        const selectedBrandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...selectedBrands];
        const selectedBrand = selectedBrandsWithAll.some((item) => item.value === stored.brand) ? (stored.brand as string) : ALL_VALUE;
        const classes = [{ id: 'All', name: 'All', value: 'All' }, ...(filterOptions?.customerClasses || []).filter((item: string) => item && item !== 'All').map((item: string) => ({ id: item, name: item, value: item }))];
        const resetDefaults = {
          company: selectedCompany,
          brand: ALL_VALUE,
          windowRange: getMonthRange(12),
          compareRange: getMonthRange(12, new Date(new Date().getFullYear(), new Date().getMonth() - 12, 1)),
          customerClass: 'All',
        };
        const nextDefaults = {
          ...resetDefaults,
          brand: selectedBrand,
          windowRange: stored.windowRange || resetDefaults.windowRange,
          compareRange: stored.compareRange || resetDefaults.compareRange,
          customerClass: classes.some((item) => item.value === stored.customerClass) ? (stored.customerClass as string) : 'All',
        };

        setCompanyChoices(companies);
        setCompanyBrandsMap(nextCompanyBrandsMap);
        setBrandChoices(selectedBrandsWithAll);
        setClassChoices(classes);
        setDefaultFilters(resetDefaults);
        reset(nextDefaults);
        setAppliedFilters(nextDefaults);
      } catch (error) {
        console.error('Failed to load complaints overview filters', error);
        setHasOverviewData(true);
      } finally {
        setFiltersLoading(false);
      }
    };

    loadFilters();
  }, []);

  useEffect(() => {
    if (!appliedFilters.company) {
      if (!filtersLoading) {
        setHasOverviewData(true);
      }
      setDataLoading(false);
      return;
    }

    setDataLoading(true);

    const loadOverview = async () => {
      try {
        const windowRange = Array.isArray(appliedFilters.windowRange) ? appliedFilters.windowRange : [];
        const compareRange = Array.isArray(appliedFilters.compareRange) ? appliedFilters.compareRange : [];
        const response = await getComplaintOverview({
          complaintCompanyId: appliedFilters.company,
          brandId: appliedFilters.brand === ALL_VALUE ? null : appliedFilters.brand,
          brandName: appliedFilters.brand === ALL_VALUE ? 'All Brands' : brandName,
          fromMonth: toMonthParam(windowRange[0]),
          toMonth: toMonthParam(windowRange[1]),
          compareFromMonth: toMonthParam(compareRange[0]),
          compareToMonth: toMonthParam(compareRange[1]),
          customerClass: appliedFilters.customerClass,
        });

        setData(response);
        setHasOverviewData(true);
      } catch (error) {
        console.error('Failed to load complaints overview', error);
        setData(buildEmptyOverviewData(appliedFilters));
        setHasOverviewData(true);
      } finally {
        setDataLoading(false);
      }
    };

    loadOverview();
  }, [appliedFilters.company, appliedFilters.brand, appliedFilters.customerClass, appliedFilters.windowRange, appliedFilters.compareRange, filtersLoading]);

  const onApply = async (values: FilterFormValues) => {
    const classes = [{ id: 'All', name: 'All', value: 'All' }, ...((filterOptionsSnapshot?.customerClasses || []) as string[]).filter((item: string) => item && item !== 'All').map((item: string) => ({ id: item, name: item, value: item }))];
    const brands = companyBrandsMap[values.company] || [];
    const brandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...brands];
    const nextValues = {
      ...values,
      brand: brandsWithAll.some((item) => item.value === values.brand) ? values.brand : ALL_VALUE,
      customerClass: classes.some((item) => item.value === values.customerClass) ? values.customerClass : 'All',
    };

    setBrandChoices(brandsWithAll);
    setClassChoices(classes);
    reset(nextValues);
    setAppliedFilters(nextValues);
    saveSharedComplaintFilters(nextValues);
  };

  const onReset = () => {
    reset(defaultFilters);
    setAppliedFilters(defaultFilters);
    saveSharedComplaintFilters(defaultFilters);
  };

  const onDeleteFilter = (label: string) => {
    const fieldMap: Record<string, keyof FilterFormValues> = {
      Company: 'company',
      Brand: 'brand',
      Window: 'windowRange',
      Compare: 'compareRange',
      Class: 'customerClass',
    };

    const field = fieldMap[label];
    if (!field) return;

    setValue(field, defaultFilters[field]);
    setAppliedFilters((current) => ({
      ...current,
      [field]: defaultFilters[field],
    }));
    saveSharedComplaintFilters({
      ...appliedFilters,
      [field]: defaultFilters[field],
    });
  };

  return (
    <div className="complaints-overview">
      <Div>
        <PageTitle title="Complaints Overview" />
      </Div>

      <Card className="ranking-module complaints-overview__module border-0">
        <Card.Body>
          {filtersLoading ? (
            <FilterCardLoader />
          ) : (
            <Form onSubmit={handleSubmit(onApply)} className="ranking-filters mb-4">
              <Div className="mb-2 ranking-section">
                <Row className="g-3 align-items-end">
                      <Col xl md={6}>
                        <Form.Group className="complaints-overview__select">
                          <Form.Label className="form-label geography-label">Company</Form.Label>
                          <Select
                            className="react-select"
                            classNamePrefix="react-select"
                            options={companyChoices.map((option) => ({ value: option.value, label: option.name }))}
                            value={selectedCompanyOption ? { value: selectedCompanyOption.value, label: selectedCompanyOption.name } : null}
                            onChange={(option: { value: string; label: string } | null) => {
                              const companyValue = option?.value || '';
                              const brands = companyBrandsMap[companyValue] || [];
                              const brandsWithAll = [{ id: ALL_VALUE, name: ALL_VALUE, value: ALL_VALUE }, ...brands];
                              setValue('company', companyValue);
                              setValue('brand', ALL_VALUE);
                              setBrandChoices(brandsWithAll);
                            }}
                            isSearchable
                            isClearable={false}
                          />
                          <input type="hidden" {...register('company')} />
                        </Form.Group>
                      </Col>
                      <Col xl md={6}>
                        <Form.Group className="complaints-overview__select">
                          <Form.Label className="form-label geography-label">Brand</Form.Label>
                          <Select
                            className="react-select"
                            classNamePrefix="react-select"
                            options={brandChoices.map((option) => ({ value: option.value, label: option.name }))}
                            value={selectedBrandOption ? { value: selectedBrandOption.value, label: selectedBrandOption.name } : null}
                            onChange={(option: { value: string; label: string } | null) => {
                              setValue('brand', option?.value || '');
                            }}
                            isSearchable
                            isClearable={false}
                          />
                          <input type="hidden" {...register('brand')} />
                        </Form.Group>
                      </Col>
                      <Col xl md={6}>
                        <FormInput
                          type="date"
                          label="Window"
                          name="windowRange"
                          control={control}
                          errors={{}}
                          range
                          onlyMonthPicker
                          readOnly
                          labelClassName="form-label geography-label"
                          placeholder="mm/yyyy - mm/yyyy"
                          validation={{
                            validate: (value: unknown) => Array.isArray(value) && value.length === 2 ? true : 'Window From and To are required',
                          }}
                        />
                      </Col>
                      <Col xl md={6}>
                        <FormInput
                          type="date"
                          label="Compare"
                          name="compareRange"
                          control={control}
                          errors={{}}
                          range
                          onlyMonthPicker
                          readOnly
                          labelClassName="form-label geography-label"
                          placeholder="mm/yyyy - mm/yyyy"
                          validation={{
                            validate: (value: unknown) => Array.isArray(value) && value.length === 2 ? true : 'Compare From and To are required',
                          }}
                        />
                      </Col>
                      <Col xl md={6}>
                        <Form.Group className="complaints-overview__select">
                          <Form.Label className="form-label geography-label">Class</Form.Label>
                          <Select
                            className="react-select"
                            classNamePrefix="react-select"
                            options={classChoices.map((option) => ({ value: option.value, label: option.name }))}
                            value={selectedClassOption ? { value: selectedClassOption.value, label: selectedClassOption.name } : null}
                            onChange={(option: { value: string; label: string } | null) => setValue('customerClass', option?.value || '')}
                            isSearchable
                            isClearable={false}
                          />
                          <input type="hidden" {...register('customerClass')} />
                        </Form.Group>
                      </Col>
                      <Col xl="auto" xs={12} className="complaints-overview__actions-col">
                        <Div className="d-flex justify-content-xl-end gap-2 complaints-overview__actions">
                          <Button type="button" variant="primary" onClick={onReset}>Reset</Button>
                          <Button type="submit" variant="primary">Apply</Button>
                        </Div>
                      </Col>
                </Row>
              </Div>
              <Div className="mb-2 ranking-section">
                <Div className="d-flex flex-wrap gap-2 complaints-overview__pill-row">
                  {selectedFilterPills
                    .filter(([, value]) => value)
                    .map(([label, value]) => (
                      <Badge key={label} className="mx-1" bg="secondary">
                        {label}: {value}
                        <i
                          className="mdi mdi-close"
                          onClick={() => onDeleteFilter(label)}
                          style={{ cursor: 'pointer', marginLeft: '5px' }}
                          role="button"
                          aria-label={`Remove ${label}`}
                        ></i>
                      </Badge>
                    ))}
                </Div>
              </Div>
            </Form>
          )}

          <Row className="g-3 mb-3">
            <Col xl={3} md={6}>
              {showContentLoading ? (
                <div className="complaints-overview__summary-card d-flex align-items-center justify-content-center" style={{ minHeight: 122 }}><img src={loader} height={36} alt="Loading" /></div>
              ) : (
                <CountSummaryCard current={data.summary.t12mComplaints} prior={data.summary.priorComplaints} windowLabel={activeWindowLabel} infoTooltip={countTooltip} />
              )}
            </Col>
            <Col xl={3} md={6}>
              {showContentLoading ? (
                <div className="complaints-overview__summary-card d-flex align-items-center justify-content-center" style={{ minHeight: 122 }}><img src={loader} height={36} alt="Loading" /></div>
              ) : (
                <SummaryCard eyebrow="Top State" primary={data.summary.topState} secondary={data.summary.topStateNote} infoTooltip={topStateTooltip} />
              )}
            </Col>
            <Col xl={3} md={6}>
              {showContentLoading ? (
                <div className="complaints-overview__summary-card d-flex align-items-center justify-content-center" style={{ minHeight: 122 }}><img src={loader} height={36} alt="Loading" /></div>
              ) : (
                <SummaryCard eyebrow="Top Complaint Type" primary={data.summary.topComplaintType} secondary={data.summary.topComplaintTypeNote} infoTooltip={topTypeTooltip} />
              )}
            </Col>
            <Col xl={3} md={6}>
              {showContentLoading ? (
                <div className="complaints-overview__summary-card d-flex align-items-center justify-content-center" style={{ minHeight: 122 }}><img src={loader} height={36} alt="Loading" /></div>
              ) : (
                <SummaryCard eyebrow="Most Affected Class" primary={data.summary.mostAffectedClass} secondary={data.summary.mostAffectedClassNote} infoTooltip={topClassTooltip} />
              )}
            </Col>
          </Row>

          <Row className="g-3 mb-3">
            <Col xl={6}>
              <ChartPanel title={`Complaints by state, ${activeWindowLabel}${currentRangeLabel ? ` (${currentRangeLabel})` : ''}`} data={data.byState} loading={showContentLoading} infoTooltip={byStateTooltip} />
            </Col>
            <Col xl={6}>
              <ChartPanel title={`Complaints by type, ${activeWindowLabel}${currentRangeLabel ? ` (${currentRangeLabel})` : ''}`} data={data.byType} loading={showContentLoading} infoTooltip={byTypeTooltip} />
            </Col>
          </Row>

          <Row className="g-3">
            <Col xl={6}>
              <ComparisonPanel title={`${activeWindowLabel}${currentRangeLabel ? ` (${currentRangeLabel})` : ''} vs ${activeCompareLabel}${priorRangeLabel ? ` (${priorRangeLabel})` : ''} by state`} data={data.stateComparison} loading={showContentLoading} infoTooltip={comparisonTooltip} />
            </Col>
            <Col xl={6}>
              <TakeawaysPanel data={data.takeaways} loading={showContentLoading} />
            </Col>
          </Row>
        </Card.Body>
      </Card>
    </div>
  );
};

export default ComplaintsOverview;
